import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import GET_ORDER_PENDING_APPROVAL_DELIVERIES from 'api/queries/GET_ORDER_PENDING_APPROVAL_DELIVERIES';
import GET_ORDER_SUPPLIERS_LIST from 'api/queries/GET_ORDER_SUPPLIERS_LIST';
import { ORDER_SUPPLIERS_PAGE_SIZE } from 'constants/pagination';
import ShowListSvg from 'images/show-list.svg';
import HideListSvg from 'images/v5/icons/align-justify.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import ChevronRightSvg from 'images/v5/icons/chevron-right.svg';
import FilterSvg from 'images/v5/icons/filter-lines.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import Button from 'layout/atoms/Button/Button';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import Loader from 'layout/atoms/Loader/Loader';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Tooltip from 'layout/atoms/Tooltip';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import Pagination from 'layout/molecules/Pagination/Pagination';
import SimpleTimeline from 'layout/molecules/SimpleTimeline';
import { openModal, selectOrderCollectionsTimelinePeriod, setOrderCollectionsTimelinePeriod, } from 'store/layoutSlice';
import { fetchOrderSuppliersPaginated, goToNextSuppliersPage, goToPreviousSuppliersPage, goToSuppliersPage, resetSuppliersPagination, selectOrderDetailsEvents, selectOrderDetailsItem, selectOrderDetailsRefetchFlags, selectOrderDetailsStatus, selectOrderDetailsSuppliers, selectOrderDetailsSuppliersCurrentPage, selectOrderDetailsSuppliersCursors, selectOrderDetailsSuppliersPageInfo, selectOrderDetailsSuppliersTotalCount, } from 'store/orderDetailsSlice';
import styles from './Collections.module.scss';
import SupplierTooltip from './SupplierTooltip';
const COLUMN_WIDTHS = { name: 200, zam: 90, dostawa: 130, status: 150, action: 30 };
function Collections() {
    var _a, _b, _c;
    const { t } = useTranslation(['orders', 'specification']);
    const dispatch = useDispatch();
    const status = useSelector(selectOrderDetailsStatus);
    const refetchFlag = useSelector(selectOrderDetailsRefetchFlags).fetchOrderVolume;
    const orderItem = useSelector(selectOrderDetailsItem);
    const orderSuppliers = useSelector(selectOrderDetailsSuppliers);
    const events = useSelector(selectOrderDetailsEvents);
    const pageInfo = useSelector(selectOrderDetailsSuppliersPageInfo);
    const currentPage = useSelector(selectOrderDetailsSuppliersCurrentPage);
    const cursors = useSelector(selectOrderDetailsSuppliersCursors);
    const totalCount = useSelector(selectOrderDetailsSuppliersTotalCount);
    const timelinePeriod = useSelector(selectOrderCollectionsTimelinePeriod);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
    const prevSupplierIdRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const pixelsPerDayMap = {
        day: 60,
        week: 30,
        month: 15,
    };
    // Initial load and refetch after delivery changes
    useEffect(() => {
        if (status.fetch === 'succeeded' &&
            status.fetchOrderVolume === 'idle' &&
            !hasInitializedRef.current) {
            hasInitializedRef.current = true;
            dispatch(fetchOrderSuppliersPaginated({
                orderId: orderItem.id,
                first: ORDER_SUPPLIERS_PAGE_SIZE,
                supplierId: selectedSupplierId,
            }));
        }
        else if (refetchFlag) {
            // Refetch after delivery changes - preserve current page
            // cursors[0] = '' (for page 1), cursors[1] = cursor for page 2, etc.
            // To refetch page N, we need after: cursors[N-1]
            let currentCursor = null;
            if (currentPage === 1) {
                currentCursor = null;
            }
            else if (currentPage > 1 && cursors.length >= currentPage) {
                currentCursor = cursors[currentPage - 1];
                if (currentCursor === '') {
                    currentCursor = null;
                }
            }
            else {
                // Fallback: if we don't have the cursor, try to use the last known cursor
                currentCursor = cursors.length > 1 ? cursors[cursors.length - 1] : null;
                if (currentCursor === '') {
                    currentCursor = null;
                }
            }
            dispatch(fetchOrderSuppliersPaginated({
                orderId: orderItem.id,
                first: ORDER_SUPPLIERS_PAGE_SIZE,
                after: currentCursor,
                supplierId: selectedSupplierId,
            }));
        }
    }, [
        dispatch,
        orderItem.id,
        status.fetch,
        status.fetchOrderVolume,
        refetchFlag,
        currentPage,
        cursors,
        selectedSupplierId,
    ]);
    // Handle supplier filter changes separately
    useEffect(() => {
        const supplierIdChanged = prevSupplierIdRef.current !== selectedSupplierId;
        if (supplierIdChanged && hasInitializedRef.current && status.fetch === 'succeeded') {
            prevSupplierIdRef.current = selectedSupplierId;
            dispatch(resetSuppliersPagination());
            dispatch(fetchOrderSuppliersPaginated({
                orderId: orderItem.id,
                first: ORDER_SUPPLIERS_PAGE_SIZE,
                supplierId: selectedSupplierId,
            }));
        }
        else if (!hasInitializedRef.current) {
            prevSupplierIdRef.current = selectedSupplierId;
        }
    }, [dispatch, orderItem.id, selectedSupplierId, status.fetch]);
    // Calculate page numbers to display (show all available pages)
    const getPageNumbers = () => {
        // If filter is applied, count actual suppliers on current page
        let effectiveTotal = totalCount;
        if (selectedSupplierId && orderSuppliers.length > 0) {
            const suppliersCount = orderSuppliers.filter((item) => item.supplier === true).length;
            // If we have less suppliers than page size, we're on last page
            if (suppliersCount < ORDER_SUPPLIERS_PAGE_SIZE) {
                effectiveTotal = suppliersCount + (currentPage - 1) * ORDER_SUPPLIERS_PAGE_SIZE;
            }
            else if (!(pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage)) {
                effectiveTotal = suppliersCount + (currentPage - 1) * ORDER_SUPPLIERS_PAGE_SIZE;
            }
        }
        if (effectiveTotal === null) {
            return [1];
        }
        let totalPages;
        if (effectiveTotal > 0) {
            totalPages = Math.ceil(effectiveTotal / ORDER_SUPPLIERS_PAGE_SIZE);
        }
        else if ((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) === false) {
            totalPages = currentPage;
        }
        else {
            totalPages = Math.max(cursors.length, currentPage);
        }
        totalPages = Math.max(totalPages, 1);
        const pages = [];
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    };
    const handleNextPage = () => {
        dispatch(goToNextSuppliersPage());
    };
    const handlePreviousPage = () => {
        dispatch(goToPreviousSuppliersPage());
    };
    const handleGoToPage = (page) => {
        dispatch(goToSuppliersPage({ pageNumber: page, supplierId: selectedSupplierId }));
    };
    const suppliersCount = orderSuppliers.filter((s) => s.supplier).length;
    const [showList, setShowList] = useState('hide');
    // Suppliers whose expansion differs from the global show/hide list mode
    const [toggledSuppliers, setToggledSuppliers] = useState(() => new Set());
    const handleShowListChange = (tab) => {
        setShowList(tab);
        setToggledSuppliers(new Set());
    };
    const isSupplierExpanded = useCallback((supplierId) => (showList === 'show') !== toggledSuppliers.has(supplierId), [showList, toggledSuppliers]);
    const toggleSupplier = useCallback((supplierId) => {
        setToggledSuppliers((prev) => {
            const next = new Set(prev);
            if (next.has(supplierId)) {
                next.delete(supplierId);
            }
            else {
                next.add(supplierId);
            }
            return next;
        });
    }, []);
    const [showColumns, setShowColumns] = useState({
        zam: false,
        dostawa: true,
        status: true,
    });
    // Fetch suppliers list for filter dropdown (lightweight query - just IDs and names)
    const { data: suppliersListData, loading: suppliersListLoading } = useQuery(GET_ORDER_SUPPLIERS_LIST, {
        variables: { id: orderItem.id },
        skip: !orderItem.id,
    });
    // Fetch the count of deliveries awaiting approval, shown as a badge on the toolbar button
    const { data: pendingApprovalData, refetch: refetchPendingApproval } = useQuery(GET_ORDER_PENDING_APPROVAL_DELIVERIES, {
        variables: { id: orderItem.id },
        skip: !orderItem.id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });
    const pendingApprovalCount = ((_b = (_a = pendingApprovalData === null || pendingApprovalData === void 0 ? void 0 : pendingApprovalData.node) === null || _a === void 0 ? void 0 : _a.pendingApprovalDeliveries) === null || _b === void 0 ? void 0 : _b.length) || 0;
    useEffect(() => {
        if (refetchFlag && orderItem.id) {
            refetchPendingApproval({ id: orderItem.id });
        }
    }, [refetchFlag, orderItem.id, refetchPendingApproval]);
    const suppliersList = React.useMemo(() => {
        var _a, _b;
        if (!((_b = (_a = suppliersListData === null || suppliersListData === void 0 ? void 0 : suppliersListData.node) === null || _a === void 0 ? void 0 : _a.orderSuppliers) === null || _b === void 0 ? void 0 : _b.edges)) {
            return [];
        }
        const uniqueSuppliers = new Map();
        suppliersListData.node.orderSuppliers.edges.forEach((edge) => {
            var _a;
            const supplier = (_a = edge.node) === null || _a === void 0 ? void 0 : _a.supplier;
            if (supplier && supplier.id && !uniqueSuppliers.has(supplier.id)) {
                uniqueSuppliers.set(supplier.id, {
                    id: supplier.id,
                    name: supplier.shortName || supplier.fullName || supplier.code || '',
                });
            }
        });
        return Array.from(uniqueSuppliers.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [suppliersListData]);
    // const oneDayInMilliseconds = 7 * 24 * 60 * 60 * 1000;
    // const weekOptions = Array.from({ length: 17 }, (_, i) => {
    //   const weekStart = moment()
    //     .startOf('week')
    //     .add(i - 8, 'weeks');
    //   const weekEnd = weekStart.clone().endOf('week');
    //   const value = weekStart.format('YYYY-MM-DD');
    //   const label = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD, YYYY')}`;
    //   return { value, label, weekStart: weekStart.clone() };
    // });
    const goToToday = () => {
        // const start = moment().startOf('week');
        // const end = moment().endOf('week').add(7, 'days');
        // if (timelineRef.current) {
        //   timelineRef.current.updateScrollCanvas(start.valueOf(), end.valueOf());
        // }
    };
    useEffect(() => {
        goToToday();
    }, []);
    // Left panel columns: name, optional data columns and the column settings action
    const columnWidths = [
        COLUMN_WIDTHS.name,
        ...(showColumns.zam ? [COLUMN_WIDTHS.zam] : []),
        ...(showColumns.dostawa ? [COLUMN_WIDTHS.dostawa] : []),
        ...(showColumns.status ? [COLUMN_WIDTHS.status] : []),
        COLUMN_WIDTHS.action,
    ];
    const gridTemplateColumns = columnWidths.map((width) => `${width}px`).join(' ');
    const labelWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    // Filter suppliers and events (no client-side supplier filtering - done on backend)
    const filteredSuppliers = useMemo(() => {
        // Variant rows follow their supplier row - show them only when the supplier is expanded
        let parentExpanded = false;
        let result = orderSuppliers.filter((item) => {
            if (item.supplier) {
                parentExpanded = isSupplierExpanded(item.id);
                return true;
            }
            return parentExpanded;
        });
        if (selectedStatusFilter) {
            const variantIdsWithStatus = orderSuppliers
                .filter((item) => item.supplier === false && item.status === selectedStatusFilter)
                .map((item) => item.id);
            const suppliersWithFilteredVariants = new Set();
            let currentSupplierId = null;
            for (let i = 0; i < orderSuppliers.length; i += 1) {
                const item = orderSuppliers[i];
                if (item.supplier === true) {
                    currentSupplierId = item.id;
                }
                else if (variantIdsWithStatus.includes(item.id) && currentSupplierId) {
                    suppliersWithFilteredVariants.add(currentSupplierId);
                }
            }
            // Filter ALL items - keep suppliers with matching variants + only variants that match the status
            result = orderSuppliers.filter((item) => {
                if (item.supplier === true) {
                    return suppliersWithFilteredVariants.has(item.id);
                }
                return item.status === selectedStatusFilter;
            });
        }
        return result;
    }, [orderSuppliers, isSupplierExpanded, selectedStatusFilter]);
    const filteredEvents = useMemo(() => (showList ? events.filter((item) => item.supplier === false) : events), [events, showList]);
    const statusLabels = useMemo(() => ({
        'error': t('COLLECTION_STATUS_DELAYED'),
        'in-progress': t('COLLECTION_STATUS_IN_DELIVERY'),
        'done': t('DELIVERED'),
        'waiting': t('COLLECTION_STATUS_WAITING'),
        'awaiting-approval': t('delivery_status_awaiting_approval'),
    }), [t]);
    // Opens the delivery planner for the variant; a supplier with many variants gets expanded instead
    const handleStatusClick = useCallback((group) => {
        var _a;
        const componentVariantId = group.supplier
            ? ((_a = group.componentVariantIds) === null || _a === void 0 ? void 0 : _a.length) === 1 && group.componentVariantIds[0]
            : group.componentVariantId;
        if (componentVariantId) {
            dispatch(openModal({ name: 'deliveryPlanner', data: { id: group.id, componentVariantId } }));
            return;
        }
        if (group.supplier && !isSupplierExpanded(group.id)) {
            toggleSupplier(group.id);
        }
    }, [dispatch, isSupplierExpanded, toggleSupplier]);
    const renderGroupLeft = useCallback((group) => {
        const progress = group.zam > 0 ? Math.round((group.dostawa / group.zam) * 100) : 0;
        const statusButton = (React.createElement("button", { type: "button", className: styles.statusButton, onClick: () => handleStatusClick(group) },
            React.createElement("span", { className: styles.statusIcon }),
            React.createElement("span", { className: styles.statusLabel }, statusLabels[group.status])));
        return (React.createElement("div", { className: classNames(styles.groupRow, styles[`status-${group.status}`]), style: { gridTemplateColumns } },
            React.createElement("div", { className: classNames(styles.cell, styles.name, {
                    [styles.supplierName]: group.supplier,
                    [styles.variant]: !group.supplier,
                }) },
                group.supplier && (React.createElement("button", { type: "button", className: classNames(styles.expandButton, {
                        [styles.expanded]: isSupplierExpanded(group.id),
                    }), onClick: () => toggleSupplier(group.id) },
                    React.createElement(ChevronRightSvg, null))),
                React.createElement(Tooltip, { theme: "inverse", content: React.createElement(SupplierTooltip, { data: { title: group.title } }) },
                    React.createElement("p", null, group.title))),
            showColumns.zam && (React.createElement("div", { className: classNames(styles.cell, styles.muted) }, Math.round(group.zam * 1000) / 1000)),
            showColumns.dostawa && (React.createElement("div", { className: classNames(styles.cell, styles.progress) },
                React.createElement("span", { className: styles.progressTrack },
                    React.createElement("span", { className: styles.progressFill, style: { width: `${Math.min(progress, 100)}%` } })),
                React.createElement("span", { className: styles.muted },
                    progress,
                    "%"))),
            showColumns.status && (React.createElement("div", { className: styles.cell }, group.statusTooltip ? (React.createElement(Tooltip, { theme: "inverse", content: group.statusTooltip, position: "top" }, statusButton)) : (statusButton))),
            React.createElement("div", null)));
    }, [
        gridTemplateColumns,
        handleStatusClick,
        isSupplierExpanded,
        showColumns,
        statusLabels,
        toggleSupplier,
    ]);
    const renderHeaderLeft = useCallback(() => {
        return (React.createElement("div", { className: classNames(styles.groupRow, styles.headerRow), style: { gridTemplateColumns } },
            React.createElement("div", { className: classNames(styles.cell, styles.name, styles.header) }, t('specification:supplier_name')),
            showColumns.zam && (React.createElement("div", { className: classNames(styles.cell, styles.header) }, t('orders'))),
            showColumns.dostawa && (React.createElement("div", { className: classNames(styles.cell, styles.header) }, t('delivered'))),
            showColumns.status && (React.createElement("div", { className: classNames(styles.cell, styles.header) }, t('status'))),
            React.createElement("div", { className: classNames(styles.smallCell, styles.header) },
                React.createElement(Dropdown, { trigger: React.createElement(Button, { size: "xs", theme: "border", shape: "squared", leftIcon: React.createElement(PlusSvg, null) }), closeOnClick: false },
                    React.createElement(Grid, { vp: 4, hp: 4, gap: 12 },
                        React.createElement(Header, { size: "xs" }, t('SHOW_COLUMNS')),
                        React.createElement(Checkbox, { label: t('orders'), value: showColumns.zam, onClick: () => setShowColumns(Object.assign(Object.assign({}, showColumns), { zam: !showColumns.zam })) }),
                        React.createElement(Checkbox, { label: t('delivered'), value: showColumns.dostawa, onClick: () => setShowColumns(Object.assign(Object.assign({}, showColumns), { dostawa: !showColumns.dostawa })) }),
                        React.createElement(Checkbox, { label: t('status'), value: showColumns.status, onClick: () => setShowColumns(Object.assign(Object.assign({}, showColumns), { status: !showColumns.status })) }))))));
    }, [gridTemplateColumns, showColumns, t]);
    // Transform events (they already have start_time, end_time, group)
    const timelineEvents = useMemo(() => filteredEvents.map((event) => {
        let delayDays = null;
        let daysUntilNextEvent = null;
        if (event.end_time && event.status === 'error') {
            const now = new Date();
            const endDate = new Date(event.end_time);
            if (endDate < now) {
                delayDays = Math.ceil((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
                const nextEvent = filteredEvents
                    .filter((e) => e.group === event.group) // Same supplier
                    .filter((e) => new Date(e.start_time) > endDate) // After this event
                    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())[0];
                if (nextEvent) {
                    const nextEventStart = new Date(nextEvent.start_time);
                    daysUntilNextEvent = Math.ceil((nextEventStart.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
                }
            }
        }
        return Object.assign(Object.assign({}, event), { groupId: event.group || event.id, delayDays,
            daysUntilNextEvent });
    }), [filteredEvents]);
    const timelineGroups = useMemo(() => filteredSuppliers.map((supplier) => ({
        id: supplier.id,
        title: supplier.title,
        supplier: supplier.supplier,
        zam: supplier.zam,
        dostawa: supplier.dostawa,
        status: supplier.status,
        statusTooltip: supplier.statusTooltip,
        componentVariantId: supplier.componentVariantId,
        componentVariantIds: supplier.componentVariantIds,
    })), [filteredSuppliers]);
    if (status.fetchOrderVolume === 'idle' || status.fetchOrderVolume === 'loading') {
        return React.createElement(Loader, null);
    }
    if (status.fetchOrderVolume === 'failed') {
        return React.createElement("p", null, "error");
    }
    return (React.createElement(Grid, { gap: 12, hp: 24, vp: 16 },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '8px',
                } },
                React.createElement(Tooltip, { content: t('EXPAND_COLLAPSE_ALL'), delay: 0 },
                    React.createElement(TabsButton, { size: "s", tabs: [
                            { id: 'hide', icon: React.createElement(HideListSvg, null), squared: true },
                            { id: 'show', icon: React.createElement(ShowListSvg, null), squared: true },
                        ], activeTab: showList, onClick: (e) => handleShowListChange(e) })),
                React.createElement(TabsButton, { size: "s", tabs: [
                        { id: 'day', label: t('common:DAY') },
                        { id: 'week', label: t('common:WEEK') },
                        { id: 'month', label: t('common:MONTH') },
                    ], activeTab: timelinePeriod, onClick: (e) => dispatch(setOrderCollectionsTimelinePeriod(e)) }),
                React.createElement(Button, { theme: "border", onClick: () => dispatch(openModal({
                        name: 'deliveryStatusAuditHistory',
                        data: { orderId: orderItem.id },
                    })) }, t('delivery_status_history')),
                React.createElement(Button, { theme: "border", onClick: () => dispatch(openModal({
                        name: 'deliveryApprovalList',
                        data: { orderId: orderItem.id },
                    })) }, pendingApprovalCount > 0
                    ? t('delivery_approval_list_with_count', { count: pendingApprovalCount })
                    : t('delivery_approval_list'))),
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                } },
                React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) }, selectedSupplierId
                        ? ((_c = suppliersList.find((s) => s.id === selectedSupplierId)) === null || _c === void 0 ? void 0 : _c.name) || t('suppliers')
                        : t('suppliers')) }, (close) => (React.createElement(React.Fragment, null,
                    React.createElement(Button, { align: "left", theme: selectedSupplierId === null ? 'fill' : 'ghost', onClick: () => {
                            setSelectedSupplierId(null);
                            close();
                        } }, t('common:all_v2')),
                    suppliersListLoading && (React.createElement("div", { style: { padding: '8px 16px' } },
                        React.createElement(Loader, null))),
                    !suppliersListLoading && suppliersList.length === 0 && (React.createElement(EmptyState, { noHeader: true, subheader: t('common:no_results_header') })),
                    !suppliersListLoading &&
                        suppliersList.length !== 0 &&
                        suppliersList.map((supplier) => (React.createElement(Button, { key: supplier.id, align: "left", theme: selectedSupplierId === supplier.id ? 'fill' : 'ghost', onClick: () => {
                                setSelectedSupplierId(supplier.id);
                                close();
                            } }, supplier.name)))))),
                React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) }, selectedStatusFilter === null
                        ? t('status')
                        : t(`STATUS_${selectedStatusFilter.replace('-', '_').toUpperCase()}`)) }, (close) => (React.createElement(React.Fragment, null,
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === null ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter(null);
                            close();
                        } }, t('common:all_v1')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'done' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('done');
                            close();
                        } }, t('STATUS_DONE')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'in-progress' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('in-progress');
                            close();
                        } }, t('STATUS_IN_PROGRESS')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'waiting' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('waiting');
                            close();
                        } }, t('STATUS_WAITING')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'error' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('error');
                            close();
                        } }, t('STATUS_ERROR'))))))),
        timelineGroups.length === 0 ? (React.createElement(EmptyState, { noHeader: true, subheader: t('common:no_results_header') })) : (React.createElement(React.Fragment, null,
            React.createElement(SimpleTimeline, { groups: timelineGroups, events: timelineEvents, 
                /* Day view: 60px per day (widest), Week view: 30px per day, Month view: 15px per day (narrowest) */
                pixelsPerDay: pixelsPerDayMap[timelinePeriod], renderGroupLeft: renderGroupLeft, renderHeaderLeft: renderHeaderLeft, labelWidth: labelWidth, timelinePeriod: timelinePeriod }),
            pageInfo && cursors.length > 0 && (React.createElement("div", { className: styles.pagination },
                React.createElement(Pagination, { currentPage: currentPage, pageNumbers: getPageNumbers(), pageSize: ORDER_SUPPLIERS_PAGE_SIZE, hasNextPage: pageInfo.hasNextPage, isLoading: status.fetchOrderVolume === 'loading', itemsCount: suppliersCount, totalCount: totalCount, onPreviousPage: handlePreviousPage, onNextPage: handleNextPage, onGoToPage: handleGoToPage })))))));
}
export default memo(Collections);
